import { Controller } from "@hotwired/stimulus"
import { observedFetch, currentLevel, LEVEL_OFFLINE, LEVEL_DEGRADED } from "../field_network_health"
import { timeoutFor } from "../field_timeout_policy"
import { logError, logInfo } from "../field_logger"
import db from "../field_db"

// Card #4108. Lets a Tasker fix the map pin of a place assigned to them, on the
// place details page. Two ways to set the position: drag the pin, or use the
// device's current location. Save applies it straight away via
// PATCH /api/v1/field/places/:id/pin (assignment is enforced server side by
// PlacePolicy#correct_pin?; this controller only decides what to show).
//
// Needs a connection to save. There is no offline queue by design: offline or
// on a weak signal the Tasker is told so plainly.
//
// After a save the Tasker's own cached copy of the place (Dexie cached_places)
// is updated so the offline map shows the new position at once.
//
// Values:
//   placeId, latitude, longitude: the place and where its pin sits now
//   openOnLoad: open the sheet on connect (map popup link)
//   poorAccuracyMetres: a fix wider than this asks the Tasker to confirm
//
// Targets: dialog, map, position, error, poorAccuracy, poorAccuracyText,
//          locationDenied, locateButton, saveButton, savedNotice, mapsLink

const LOCATION_KEY      = "field_user_location"
const DEFAULT_CENTRE    = [133.8, -25.7] // middle of Australia, used only when nothing better is known
const PLACE_ZOOM        = 17
const DEFAULT_ZOOM      = 3.5
const CONNECTION_NEEDED = "You need a connection to move this pin. Try again when you have signal."

export default class extends Controller {
  static values = {
    placeId: Number,
    latitude: Number,
    longitude: Number,
    openOnLoad: Boolean,
    poorAccuracyMetres: { type: Number, default: 50 }
  }
  static targets = [
    "dialog", "map", "position", "error", "poorAccuracy", "poorAccuracyText",
    "locationDenied", "locateButton", "saveButton", "savedNotice", "mapsLink"
  ]

  connect() {
    this._reset()
    if (this.openOnLoadValue) this.open()
  }

  disconnect() {
    this._destroyMap()
  }

  // -- actions --

  open() {
    this._reset()
    if (this.hasSavedNoticeTarget) this.savedNoticeTarget.classList.add("hidden")
    this.dialogTarget.showModal()
    // The map needs a sized container, so build it once the sheet is showing.
    requestAnimationFrame(() => this._buildMap())
  }

  cancel() {
    this.dialogTarget.close()
  }

  // Runs on the dialog's close event (Cancel, Esc, or after a save).
  reset() {
    this._destroyMap()
    this._reset()
  }

  useCurrentLocation() {
    this._hideMessages()

    if (!("geolocation" in navigator)) {
      this._showError("This device can't share its location. Drag the pin instead.")
      return
    }

    this._setLocating(true)
    navigator.geolocation.getCurrentPosition(
      (position) => this._gotFix(position),
      (error) => this._fixFailed(error),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    )
  }

  // The Tasker has seen the weak accuracy warning and wants this position anyway.
  acceptPoorAccuracy() {
    this._poorAccuracyAccepted = true
    this.poorAccuracyTarget.classList.add("hidden")
    this._render()
  }

  async save() {
    if (!this._position) return
    this._hideMessages()

    const level = currentLevel()
    if (level === LEVEL_OFFLINE) {
      this._showError(CONNECTION_NEEDED)
      return
    }
    if (level === LEVEL_DEGRADED) {
      this._showError("Your signal is weak. Saving may take a moment.", "info")
    }

    this.saveButtonTarget.disabled = true
    const csrfToken = document.querySelector("[name='csrf-token']")?.content

    try {
      const response = await observedFetch(`/api/v1/field/places/${this.placeIdValue}/pin`, {
        method: "PATCH",
        headers: { "Accept": "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify({
          latitude: this._position.lat,
          longitude: this._position.lng,
          method: this._method,
          accuracy_metres: this._accuracy
        }),
        signal: AbortSignal.timeout(timeoutFor("pin_write", level))
      }, { kind: "json_write", sizeClass: "small" })

      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        await this._saved(data)
      } else if (response.status === 422 || response.status === 403) {
        this._showError(data.error || "The pin couldn't be moved.")
      } else {
        throw new Error(`Server error: ${response.status}`)
      }
    } catch (error) {
      logError("app", "Pin correction failed", {
        placeId: this.placeIdValue, message: error?.message || String(error)
      }).catch(() => {})
      this._showError(CONNECTION_NEEDED)
    } finally {
      this._render()
    }
  }

  // -- private: state --

  _reset() {
    this._position = null            // { lat, lng } once the Tasker has set one
    this._method = "drag"
    this._accuracy = null
    this._poorAccuracyAccepted = true
    if (this.hasPositionTarget) this.positionTarget.textContent = "Pin: not moved yet"
    this._hideMessages()
    this._render()
  }

  _render() {
    if (!this.hasSaveButtonTarget) return
    this.saveButtonTarget.disabled = !this._position || !this._poorAccuracyAccepted
  }

  _setLocating(locating) {
    if (!this.hasLocateButtonTarget) return
    this.locateButtonTarget.disabled = locating
    this.locateButtonTarget.textContent = locating ? "Finding your location…" : "Use my current location"
  }

  _hideMessages() {
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
    if (this.hasPoorAccuracyTarget) this.poorAccuracyTarget.classList.add("hidden")
    if (this.hasLocationDeniedTarget) this.locationDeniedTarget.classList.add("hidden")
  }

  _showError(message, tone = "error") {
    const isError = tone === "error"
    this.errorTarget.textContent = message
    this.errorTarget.classList.toggle("text-red-700", isError)
    this.errorTarget.classList.toggle("bg-red-50", isError)
    this.errorTarget.classList.toggle("border-red-200", isError)
    this.errorTarget.classList.toggle("text-amber-800", !isError)
    this.errorTarget.classList.toggle("bg-amber-50", !isError)
    this.errorTarget.classList.toggle("border-amber-200", !isError)
    this.errorTarget.classList.remove("hidden")
  }

  // -- private: map --

  _buildMap() {
    if (this._map) return

    const token = document.querySelector('meta[name="mapbox-token"]')?.content
    if (!token || typeof mapboxgl === "undefined") {
      this._showError("The map couldn't load. " + CONNECTION_NEEDED)
      return
    }

    mapboxgl.accessToken = token
    const hasPlace = Number.isFinite(this.latitudeValue) && Number.isFinite(this.longitudeValue) &&
                     !(this.latitudeValue === 0 && this.longitudeValue === 0)
    const lastFix = this._lastKnownLocation()
    const centre = hasPlace ? [this.longitudeValue, this.latitudeValue]
                 : lastFix  ? [lastFix.lng, lastFix.lat]
                 : DEFAULT_CENTRE

    this._map = new mapboxgl.Map({
      container: this.mapTarget,
      style: "mapbox://styles/mapbox/streets-v12",
      center: centre,
      zoom: hasPlace || lastFix ? PLACE_ZOOM : DEFAULT_ZOOM
    })
    this._map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right")

    this._marker = new mapboxgl.Marker({ draggable: true }).setLngLat(centre).addTo(this._map)
    this._marker.on("dragend", () => {
      const { lat, lng } = this._marker.getLngLat()
      this._method = "drag"
      this._accuracy = null
      this._poorAccuracyAccepted = true
      this.poorAccuracyTarget.classList.add("hidden")
      this._setPosition(lat, lng)
    })
  }

  _destroyMap() {
    this._marker?.remove()
    this._map?.remove()
    this._marker = null
    this._map = null
  }

  _setPosition(lat, lng) {
    this._position = { lat: Number(lat.toFixed(5)), lng: Number(lng.toFixed(5)) }
    this.positionTarget.textContent = `Pin: ${this._position.lat}, ${this._position.lng}`
    this._render()
  }

  _lastKnownLocation() {
    try { return JSON.parse(localStorage.getItem(LOCATION_KEY)) } catch { return null }
  }

  // -- private: current location --

  _gotFix(position) {
    this._setLocating(false)
    const { latitude, longitude, accuracy } = position.coords

    this._method = "current_location"
    this._accuracy = Math.round(accuracy)
    this._setPosition(latitude, longitude)

    if (this._map) {
      this._marker.setLngLat([longitude, latitude])
      this._map.easeTo({ center: [longitude, latitude], zoom: PLACE_ZOOM })
    }

    if (accuracy > this.poorAccuracyMetresValue) {
      this._poorAccuracyAccepted = false
      this.poorAccuracyTextTarget.textContent =
        `Your location is only accurate to about ${this._accuracy} m. Try again outside, or use this position if you are sure it is at the store.`
      this.poorAccuracyTarget.classList.remove("hidden")
    } else {
      this._poorAccuracyAccepted = true
      this.positionTarget.textContent += ` (accurate to about ${this._accuracy} m)`
    }
    this._render()
  }

  _fixFailed(error) {
    this._setLocating(false)
    // code 1 = permission denied, 2 = position unavailable, 3 = timeout
    if (error?.code === 1) {
      this.locationDeniedTarget.classList.remove("hidden")
    } else {
      this._showError("Couldn't get your location. Try again, or drag the pin instead.")
    }
  }

  // -- private: after a save --

  async _saved(data) {
    // Update the Tasker's own cached copy so the offline map shows the new
    // position at once. Best effort: the server already has it.
    try {
      await db.cached_places.update(this.placeIdValue, { latitude: data.latitude, longitude: data.longitude })
    } catch (error) {
      logError("app", "Pin saved but the cached place was not updated", {
        placeId: this.placeIdValue, message: error?.message || String(error)
      }).catch(() => {})
    }

    logInfo("app", "Place pin moved by tasker", {
      placeId: this.placeIdValue, method: this._method, movedMetres: data.moved_metres
    }).catch(() => {})

    this.latitudeValue = data.latitude
    this.longitudeValue = data.longitude
    this.mapsLinkTargets.forEach(link => { link.href = `https://maps.google.com/?q=${data.latitude},${data.longitude}` })
    this.savedNoticeTarget.classList.remove("hidden")
    this.dialogTarget.close()
  }
}
